<template>
  <MetaInfo
    :title="pageMeta.title"
    :description="pageMeta.description"
    :image="pageMeta.image"
  />
  <div v-if="ourBlog">
    <navigation />

    <PageHeader
      :title="ourBlog.blogTitle"
      :category="displayCategory"
      :description="articleDescription"
      :image="heroImage"
      :publishedAt="ourBlog.createdAt"
    />

    <section class="container-fluid blog-article">
      <div class="row">
        <div class="col-xs-12">
          <div class="blog-article__layout">
            <aside class="blog-article__rail">
              <BlogTableOfContents :headings="article.headings" />
            </aside>
            <div class="blog-article__body">
              <article>
                <div
                  v-if="article.introHtml"
                  class="longform"
                  v-html="article.introHtml"
                />
                <BlogTldr :html="article.tldrHtml" />
                <div
                  v-if="article.bodyHtml"
                  class="longform"
                  v-html="article.bodyHtml"
                />
              </article>
              <UpNextListing
                v-if="upNext"
                :blogData="upNext.blog"
                :isNext="true"
                :nextDescription="upNext.linkDescription"
              />
            </div>
            <div class="blog-article__share">
              <BlogSocialShare
                :currentPageUrl="blogPostUrl"
                :pageTitle="shareTitle"
                :postSummary="shareDescription"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <site-footer />
  </div>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import PageHeader from "@/components/Blog/BlogArticleHeader";
import SiteFooter from "@/components/Blog/BlogFooter";
import UpNextListing from "@/components/Blog/BlogArticleUpNextListing";
import BlogSocialShare from "@/components/Blog/BlogSocialShare";
import BlogTableOfContents from "@/components/Blog/BlogTableOfContents";
import BlogTldr from "@/components/Blog/BlogTldr";
import textHelpers from "@/functions/text";
import blogArticle from "@/functions/blogArticle";
import blogUrl from "@/functions/blogUrl";
import BlogCategories from "@/functions/blogCategories";
import axios from "axios";

const BLOG_API = "https://api.transpond.io/api/blog";
const PLACEHOLDER_IMAGE = "https://yourimage.com";

function isValidArticle(blog) {
  return blog && typeof blog === "object" && !Array.isArray(blog) && blog.id != null;
}

function getLinkId(link) {
  if (link == null || link === "") {
    return null;
  }
  return link;
}

function hasFeatureImage(url) {
  return Boolean(url && url !== PLACEHOLDER_IMAGE);
}

async function fetchBlogById(id) {
  const response = await axios.get(`${BLOG_API}/${id}`);
  const blog = response.data.Blogs;
  return isValidArticle(blog) ? blog : null;
}

async function fetchBlogList() {
  const response = await axios.get(BLOG_API, { params: { summary: 1 } });
  const blogs = response.data.Blogs;
  return Array.isArray(blogs) ? blogs.filter(isValidArticle) : [];
}

function findNextInList(articles, currentId) {
  const index = articles.findIndex(
    (article) => String(article.id) === String(currentId)
  );
  if (index < 0 || index >= articles.length - 1) {
    return null;
  }
  return articles[index + 1];
}

export default {
  name: "BlogArticleFull",
  components: {
    MetaInfo,
    Navigation,
    PageHeader,
    SiteFooter,
    UpNextListing,
    BlogSocialShare,
    BlogTableOfContents,
    BlogTldr,
  },
  data() {
    return {
      ourBlog: null,
      upNext: null,
    };
  },
  computed: {
    article() {
      if (!this.ourBlog) {
        return blogArticle.prepareBlogArticle("");
      }
      return blogArticle.prepareBlogArticle(textHelpers.cleanHtml(this.ourBlog.content));
    },
    blogPostUrl() {
      return this.ourBlog ? window.location.href : "";
    },
    heroImage() {
      if (!this.ourBlog) return "";
      return hasFeatureImage(this.ourBlog.featureImageUrl)
        ? this.ourBlog.featureImageUrl
        : "";
    },
    displayCategory() {
      if (!this.ourBlog) return "";
      return BlogCategories.tagsFor(this.ourBlog).join(", ");
    },
    shareTitle() {
      if (!this.ourBlog) return "";
      return `${this.ourBlog.blogTitle} - Transpond Blog`;
    },
    articleDescription() {
      if (!this.ourBlog || !this.ourBlog.meta) return "";
      return this.ourBlog.meta.description || "";
    },
    shareDescription() {
      if (!this.ourBlog) return "";
      return (
        this.articleDescription ||
        `Find out about ${this.ourBlog.blogTitle} at the Transpond Blog`
      );
    },
    pageMeta() {
      if (!this.ourBlog) {
        return { title: "", description: "", image: null };
      }
      const seo = this.ourBlog.meta || {};
      return {
        title: seo.title || this.shareTitle,
        description: seo.description || this.shareDescription,
        image: this.heroImage || null,
      };
    },
  },
  created() {
    this.loadArticle();
  },
  methods: {
    async resolveUpNext(article, list) {
      const linkDescription = article.linkDescription || null;
      const linkId = getLinkId(article.link);

      if (linkId) {
        const blog = await fetchBlogById(linkId).catch(() => null);
        if (blog) {
          this.upNext = { blog, linkDescription };
        }
        return;
      }

      const articles = list || (await fetchBlogList().catch(() => []));
      const next = findNextInList(articles, article.id);
      if (!next) {
        return;
      }
      const blog = next.content ? next : await fetchBlogById(next.id).catch(() => null);
      if (blog) {
        this.upNext = { blog, linkDescription };
      }
    },
    // Keep the query string so UTM params on old or wrong links survive the redirect
    redirectTo(path, hash = "") {
      this.$router.replace({ path, query: this.$route.query, hash });
    },
    async loadArticle() {
      try {
        const list = await fetchBlogList();
        const match = blogUrl.findFromPath(list, this.$route.path);
        if (!match) {
          this.redirectTo("/blog");
          return;
        }

        const canonicalPath = blogUrl.articlePath(match);
        if (this.$route.path.replace(/\/+$/, "") !== canonicalPath.replace(/\/+$/, "")) {
          this.redirectTo(canonicalPath, this.$route.hash);
          return;
        }

        const article = match.content ? match : await fetchBlogById(match.id);
        if (!article) {
          this.redirectTo("/blog");
          return;
        }

        this.ourBlog = article;
        await this.resolveUpNext(article, list);
      } catch {
        this.redirectTo("/blog");
      }
    },
  },
};
</script>
